import React from "react";
import { Card } from "@heroui/react";
import { BuildingOfficeIcon, UserIcon } from "@heroicons/react/24/outline";
import { TFunction } from "react-i18next";
import hoowlrLogo from "../../assets/images/logo.svg";
import loginBanner from "../../assets/images/login-banner.png";

interface RoleSelectionStageProps {
    t: TFunction<"signUpSetupStages", undefined>;
    handleRoleSelection: (role: "FIGHTER" | "ORGANIZATION") => void;
}

const RoleSelectionStage: React.FC<RoleSelectionStageProps> = ({ t, handleRoleSelection }) => {
    const roles = [
        {
            role: "FIGHTER" as const,
            Icon: UserIcon,
            title: t("roles.fighter.title"),
            description: t("roles.fighter.description"),
        },
        {
            role: "ORGANIZATION" as const,
            Icon: BuildingOfficeIcon,
            title: t("roles.organization.title"),
            description: t("roles.organization.description"),
        },
    ];

    return (
        <div className="flex min-h-dvh w-full bg-white font-['Plus_Jakarta_Sans',sans-serif] text-[#18181B]">
            <div className="flex min-w-0 flex-1 flex-col">
                <div
                    className="
                        flex min-h-dvh flex-col justify-between
                        px-6 py-6 sm:px-12 sm:py-8
                        lg:py-9
                        [@media(min-height:701px)_and_(max-height:800px)]:lg:py-6
                        [@media(max-height:700px)]:lg:py-3
                    "
                >
                    <div className="flex w-full items-center justify-between">
                        <img
                            src={hoowlrLogo}
                            alt="Hoowlr"
                            className="
                                h-9 w-auto object-contain object-left
                                lg:h-16 lg:w-48
                                [@media(min-height:701px)_and_(max-height:800px)]:lg:h-14
                                [@media(max-height:700px)]:lg:h-10
                            "
                        />
                    </div>

                    <div className="flex flex-1 flex-col items-center justify-center my-auto w-full py-2">
                        <div className="flex w-full flex-col items-center gap-1 sm:gap-2 text-center mb-4 sm:mb-6 lg:mb-8 [@media(max-height:700px)]:mb-3">
                            <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold lg:font-extrabold leading-tight lg:leading-10 text-[#18181B]">
                                {t("welcome.title")}
                            </h1>
                            <p className="text-xs sm:text-sm lg:text-base leading-4 lg:leading-6 text-[#71717A]">
                                {t("welcome.subtitle")}
                            </p>
                        </div>

                        <div className="mx-auto flex w-full max-w-96 flex-col gap-3 sm:gap-4 lg:gap-6">
                            {roles.map(({ role, Icon, title, description }) => (
                                <Card
                                    key={role}
                                    isPressable
                                    shadow="none"
                                    onPress={() => handleRoleSelection(role)}
                                    className="w-full items-center gap-2.5 sm:gap-3 rounded-2xl lg:rounded-3xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-transparent p-3.5 sm:p-4 shadow-xs lg:shadow-none transition-all hover:bg-slate-50 lg:hover:bg-gray-50 active:scale-[0.99]"
                                >
                                    <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-[#18181B]" />
                                    <div className="flex flex-col items-center gap-0.5 sm:gap-1 text-center">
                                        <span className="text-xs sm:text-sm font-semibold lg:font-medium leading-4 lg:leading-5 text-[#18181B]">{title}</span>
                                        <span className="text-[11px] sm:text-xs font-normal leading-4 text-[#71717A]">{description}</span>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>

                    <p className="pt-2 pb-1 text-center text-[11px] sm:text-xs font-normal leading-4 text-[#71717A]">
                        {t("footer_copyright", "© Hoowlr 2026, All rights reserved")}
                    </p>
                </div>
            </div>

            <div className="hidden shrink-0 p-9 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:w-[45%] xl:w-[55%] 2xl:w-[62%]">
                <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-[2rem] bg-[#1F6BFF]">
                    <img src={loginBanner} alt="" className="max-h-full max-w-full object-contain" />
                </div>
            </div>
        </div>
    );
};

export default RoleSelectionStage;